import React, { useState, useRef, useEffect } from "react";
import "./OTPModal.styles.scss";

const OTPModal = ({ isOpen, onClose, onVerify, phoneNumber, initialTimer, onResend, error, onErrorClear }) => {
    const [otp, setOtp] = useState(new Array(6).fill(""));
    const [timeLeft, setTimeLeft] = useState(0);
    const [resendPending, setResendPending] = useState(false);
    const timerRef = useRef(null);
    const inputRefs = useRef([]);

    const startTimer = (seconds) => {
        if (timerRef.current) clearInterval(timerRef.current);
        setResendPending(false);
        setTimeLeft(seconds);
        timerRef.current = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    useEffect(() => {
        if (isOpen) {
            inputRefs.current[0]?.focus();
            setOtp(new Array(6).fill(""));
            startTimer(initialTimer ?? 180);
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            setTimeLeft(0);
            setResendPending(false);
            setOtp(new Array(6).fill(""));
        }
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [isOpen]);

    // Restart timer when initialTimer changes (e.g. after resend delivers a fresh expiry)
    useEffect(() => {
        if (isOpen && initialTimer) {
            startTimer(initialTimer);
        }
    }, [initialTimer]);

    const handleChange = (element, index) => {
        const value = element.value.replace(/[^0-9]/g, "");
        if (!value) return;
        if (onErrorClear) onErrorClear();
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        if (index < otp.length - 1) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData("text").replace(/\D/g, "");
        if (!pasteData) return;
        const newOtp = [...otp];
        pasteData.split("").slice(0, 6).forEach((char, i) => {
            newOtp[i] = char;
        });
        setOtp(newOtp);
        inputRefs.current[Math.min(pasteData.length, 6) - 1]?.focus();
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace") {
            e.preventDefault();
            const newOtp = [...otp];
            if (newOtp[index]) {
                newOtp[index] = "";
                setOtp(newOtp);
            } else if (index > 0) {
                inputRefs.current[index - 1].focus();
                newOtp[index - 1] = "";
                setOtp(newOtp);
            }
        } else if (e.key === "Enter") {
            e.preventDefault();
            const enteredOtp = otp.join("");
            if (enteredOtp.length === 6) {
                handleVerify();
            }
        }
    };

    const handleVerify = () => {
        const enteredOtp = otp.join("");
        onVerify(enteredOtp);
    };

    const handleResend = () => {
        setOtp(new Array(6).fill(""));
        setTimeLeft(0);
        setResendPending(true);
        if (onErrorClear) onErrorClear();
        inputRefs.current[0]?.focus();
        if (onResend) onResend();
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    if (!isOpen) return null;

    return (
        <div className="otp-modal-overlay">
            <div className="otp-modal">
                <h2>Enter OTP</h2>
                <p>Please enter the 6-digit OTP sent to your phone {phoneNumber}</p>
                <div className="otp-inputs">
                    {otp.map((data, index) => (
                        <input
                            key={index}
                            type="text"
                            maxLength="1"
                            value={data}
                            ref={(el) => (inputRefs.current[index] = el)}
                            onChange={(e) => handleChange(e.target, index)}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            onPaste={handlePaste}
                        />
                    ))}
                </div>
                <div className="otp-timer">
                    {resendPending ? (
                        <span>Sending new code...</span>
                    ) : timeLeft > 0 ? (
                        <span>Code expires in {formatTime(timeLeft)}</span>
                    ) : (
                        <span>Code expired</span>
                    )}
                </div>
                {error && <p className="otp-error">{error}</p>}
                <div className="otp-actions">
                    <button className="verify-btn" onClick={handleVerify}>
                        Verify
                    </button>
                    {onResend && timeLeft === 0 && !resendPending && (
                        <button className="resend-btn" onClick={handleResend}>
                            Resend OTP
                        </button>
                    )}
                    <button className="close-btn" onClick={onClose}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OTPModal;
